import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import { Users, Search, Plus, Trash2, Globe } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { clearCache, CACHE_KEYS } from '../../services/cacheService';
import { useTranslation, Trans } from 'react-i18next';

interface Recipient {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  company_name: string | null;
  position: string | null;
  notes: string | null;
  is_shared: boolean;
  locale: string;
  created_by: number;
  created_at: string;
}

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  company_name: '',
  position: '',
  notes: '',
  is_shared: false,
  locale: 'en',
};

const Recipients: React.FC = () => {
  const { t } = useTranslation(['recipients', 'common']);
  const storedUser = localStorage.getItem('user');
  const isBusiness = storedUser ? JSON.parse(storedUser).customer_type === 'business' : false;
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Form dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState<Recipient | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [recipientToDelete, setRecipientToDelete] = useState<Recipient | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchRecipients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());

      const res = await fetch(`${API_BASE_URL}/recipients?${params}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        throw new Error(`Error ${res.status}`);
      }
      const data = await res.json();
      setRecipients(data.recipients || []);
    } catch (err: any) {
      setError(err.message || t('common:errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, t]);

  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  // Open create dialog
  const handleCreate = () => {
    setEditingRecipient(null);
    setForm(emptyForm);
    setFormErrors({});
    setFormOpen(true);
  };

  // Open edit dialog
  const handleEdit = (recipient: Recipient) => {
    setEditingRecipient(recipient);
    setForm({
      name: recipient.name,
      email: recipient.email,
      phone: recipient.phone || '',
      company_name: recipient.company_name || '',
      position: recipient.position || '',
      notes: recipient.notes || '',
      is_shared: recipient.is_shared,
      locale: recipient.locale || 'en',
    });
    setFormErrors({});
    setFormOpen(true);
  };

  // Submit form
  const handleSubmit = async () => {
    setSaving(true);
    setFormErrors({});
    try {
      const isEdit = !!editingRecipient;
      const url = isEdit
        ? `${API_BASE_URL}/recipients/${editingRecipient!.id}`
        : `${API_BASE_URL}/recipients`;

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        const data = await res.json();
        if (data.errors) {
          const errs: Record<string, string> = {};
          Object.entries(data.errors).forEach(([k, v]) => {
            errs[k] = Array.isArray(v) ? v[0] : String(v);
          });
          setFormErrors(errs);
        } else {
          throw new Error(data.message || t('common:errors.generic'));
        }
        return;
      }

      setFormOpen(false);
      clearCache(CACHE_KEYS.RECIPIENTS);
      fetchRecipients();
    } catch (err: any) {
      setFormErrors({ _general: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDeleteClick = (recipient: Recipient) => {
    setRecipientToDelete(recipient);
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!recipientToDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/recipients/${recipientToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        setDeleteError(data.message || t('common:errors.generic'));
        return;
      }
      setDeleteOpen(false);
      setRecipientToDelete(null);
      clearCache(CACHE_KEYS.RECIPIENTS);
      fetchRecipients();
    } catch (err: any) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between gap-4 items-start lg:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('recipients:page_title')}</h1>
          <p className="text-muted-foreground">{t('recipients:page_subtitle')}</p>
        </div>
        <div className="flex flex-row gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('recipients:search_placeholder')}
              className="pl-9 w-72"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" /> {t('recipients:new_recipient')}
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              <p>{error}</p>
              <Button variant="outline" onClick={fetchRecipients} className="mt-4">{t('common:actions.try_again')}</Button>
            </div>
          ) : recipients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">{t('recipients:no_recipients_found')}</h3>
              <p className="text-muted-foreground">{t('recipients:no_recipients_hint')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('recipients:table_name')}</TableHead>
                    <TableHead>{t('recipients:table_email')}</TableHead>
                    <TableHead>{t('recipients:table_phone')}</TableHead>
                    <TableHead>{t('recipients:table_company')}</TableHead>
                    {isBusiness && <TableHead>{t('recipients:table_shared')}</TableHead>}
                    <TableHead className="text-center">{t('recipients:table_actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recipients.map((recipient) => (
                    <TableRow key={recipient.id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleEdit(recipient)}>
                      <TableCell className="font-medium">{recipient.name}</TableCell>
                      <TableCell className="text-muted-foreground">{recipient.email}</TableCell>
                      <TableCell className="text-muted-foreground">{recipient.phone || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{recipient.company_name || '—'}</TableCell>
                      {isBusiness && (
                        <TableCell>
                          <Badge variant="outline" className={recipient.is_shared ? 'bg-blue-50 text-blue-700' : 'bg-slate-50'}>
                            {recipient.is_shared ? t('recipients:shared_yes') : t('recipients:shared_no')}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-red-50" title={t('common:actions.delete')} onClick={() => handleDeleteClick(recipient)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRecipient ? t('recipients:edit_recipient') : t('recipients:create_recipient')}</DialogTitle>
            <DialogDescription>
              {editingRecipient ? t('recipients:edit_description') : t('recipients:create_description')}
            </DialogDescription>
          </DialogHeader>

          {formErrors._general && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{formErrors._general}</div>
          )}

          <div className="grid gap-4 py-2">
            <div>
              <Label htmlFor="rec_name">{t('recipients:form_name')} *</Label>
              <Input id="rec_name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
            </div>
            <div>
              <Label htmlFor="rec_email">{t('recipients:form_email')} *</Label>
              <Input id="rec_email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rec_phone">{t('recipients:form_phone')}</Label>
                <Input id="rec_phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                {formErrors.phone && <p className="text-xs text-red-500 mt-1">{formErrors.phone}</p>}
              </div>
              <div>
                <Label htmlFor="rec_position">{t('recipients:form_position')}</Label>
                <Input id="rec_position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
                {formErrors.position && <p className="text-xs text-red-500 mt-1">{formErrors.position}</p>}
              </div>
            </div>
            <div>
              <Label htmlFor="rec_company">{t('recipients:form_company')}</Label>
              <Input id="rec_company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
              {formErrors.company_name && <p className="text-xs text-red-500 mt-1">{formErrors.company_name}</p>}
            </div>
            <div>
              <Label htmlFor="rec_notes">{t('recipients:form_notes')}</Label>
              <Textarea id="rec_notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              {formErrors.notes && <p className="text-xs text-red-500 mt-1">{formErrors.notes}</p>}
            </div>
            <div>
              <Label htmlFor="rec_locale" className="flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5 text-slate-400" /> {t('recipients:form_language')}
              </Label>
              <Select value={form.locale} onValueChange={(val) => setForm({ ...form, locale: val })}>
                <SelectTrigger id="rec_locale" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">{t('recipients:form_language_en')}</SelectItem>
                  <SelectItem value="fr">{t('recipients:form_language_fr')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isBusiness && (
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="rec_shared">{t('recipients:form_shared')}</Label>
                  <p className="text-xs text-muted-foreground">{t('recipients:form_shared_hint')}</p>
                </div>
                <Switch id="rec_shared" checked={form.is_shared} onCheckedChange={(checked) => setForm({ ...form, is_shared: checked })} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={saving}>{t('common:actions.cancel')}</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? t('common:actions.saving') : editingRecipient ? t('common:actions.update') : t('common:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('recipients:delete_recipient')}</DialogTitle>
            <DialogDescription>
              <Trans
                i18nKey="recipients:delete_confirm"
                values={{ name: recipientToDelete?.name }}
                components={{ strong: <strong /> }}
              />
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded">{deleteError}</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>{t('common:actions.cancel')}</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? t('common:actions.deleting') : t('common:actions.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Recipients;
